/**
 * Cookie/privacidade banner — LGPD art. 8º (consentimento informado).
 *
 * Mostra na primeira visita até user aceitar/recusar. Persiste em localStorage
 * `runna_cookie_consent`: { status: 'accepted' | 'rejected' | 'dismissed', at: ISOString }.
 *
 * Hoje a app só usa cookies essenciais (sessão Supabase, mirror cross-subdomain
 * e impersonation stash). Sem analytics 3rd-party, sem ads. Banner é informativo
 * + link pra política. Se no futuro adicionar GA/Hotjar/etc, expandir aqui pra
 * gate scripts via consent.
 */

import { useEffect, useState } from 'react'
import { Cookie, X } from 'lucide-react'

const STORAGE_KEY = 'runna_cookie_consent'

type ConsentStatus = 'accepted' | 'rejected' | 'dismissed'

interface ConsentRecord {
  status: ConsentStatus
  at: string
}

function readConsent(): ConsentRecord | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    return JSON.parse(raw) as ConsentRecord
  } catch {
    return null
  }
}

function writeConsent(status: ConsentStatus) {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ status, at: new Date().toISOString() } satisfies ConsentRecord),
    )
  } catch {
    /* localStorage indisponível, segue sem registrar */
  }
}

export default function CookieBanner() {
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    // Atrasa render pra evitar layout shift no first paint
    const t = setTimeout(() => {
      const consent = readConsent()
      if (!consent) setVisible(true)
    }, 600)
    return () => clearTimeout(t)
  }, [])

  if (!visible) return null

  const handleAccept = () => {
    writeConsent('accepted')
    setVisible(false)
  }

  const handleDismiss = () => {
    writeConsent('dismissed')
    setVisible(false)
  }

  return (
    <div
      role="dialog"
      aria-labelledby="cookie-banner-title"
      aria-describedby="cookie-banner-desc"
      className="fixed bottom-4 left-4 right-4 z-50 max-w-md md:left-auto md:right-4 md:bottom-4"
    >
      <div className="bg-card border border-card-border rounded-2xl shadow-2xl p-4 backdrop-blur-md">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-xl bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
            <Cookie className="w-4.5 h-4.5" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 id="cookie-banner-title" className="font-heading text-[0.95rem] font-bold text-bright mb-1">
              Privacidade
            </h3>
            <p id="cookie-banner-desc" className="text-[0.78rem] text-text leading-relaxed mb-3">
              A Runnaflow usa apenas cookies essenciais pra manter você logado e isolar dados
              do seu tenant. Não usamos cookies de rastreamento ou publicidade.{' '}
              <a
                href="/privacidade"
                className="text-primary hover:underline font-medium"
                target="_blank"
                rel="noreferrer"
              >
                Ler política completa
              </a>
              .
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={handleAccept}
                className="px-3 py-1.5 rounded-lg bg-primary text-white text-[0.78rem] font-semibold cursor-pointer hover:brightness-110 transition-all"
              >
                Entendi
              </button>
              <button
                onClick={handleDismiss}
                className="px-3 py-1.5 rounded-lg text-dim text-[0.78rem] font-medium cursor-pointer hover:text-bright transition-colors"
              >
                Fechar
              </button>
            </div>
          </div>
          <button
            onClick={handleDismiss}
            className="text-dim hover:text-bright transition-colors -mt-1 -mr-1 p-1 rounded-md cursor-pointer"
            aria-label="Fechar banner"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  )
}
